import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  User,
  Mail,
  Phone,
  PhoneCall,
  Calendar,
  FileText,
  AlertTriangle,
  CreditCard,
  ExternalLink,
  Copy,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Brain,
  Scale,
  ClipboardList,
  RefreshCw,
  Trophy,
  Euro,
  MessageSquare,
  Upload,
  X,
  Images,
} from 'lucide-react';
import DocumentPreview from './DocumentPreview';
import DocumentViewer from './DocumentViewer';
import LV2Diagnostic from './LV2Diagnostic';
import LV3LegalAnalysis from './LV3LegalAnalysis';
import DemandePiecesLetter from './DemandePiecesLetter';
import SaisineJexPack from './SaisineJexPack';
import CrmCallTracking from './CrmCallTracking';
import { transformScore, isScoreExploitable } from './ScoreDisplay';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';

const LEAD_CALL_STATUS_LABELS = { qualifie: 'Qualifié', injoignable: 'Injoignable' };

const AdminDossierDetail = ({ dossier, open, onClose, onDossierUpdated }) => {
  const [showLV1, setShowLV1] = useState(true);
  const [showLV2, setShowLV2] = useState(false);
  const [showLV3, setShowLV3] = useState(false);
  const [isRetriggering, setIsRetriggering] = useState(false);
  // Ajout de pièces complémentaires (prospect qui envoie des documents après un appel)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [relaunchAfterUpload, setRelaunchAfterUpload] = useState(true);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const fileInputRef = React.useRef(null);
  // Visualiseur plein écran : index de la pièce affichée, null = fermé
  const [viewerIndex, setViewerIndex] = useState(null);
  // Rotation d'affichage des pièces (photo envoyée de travers) : file_id -> degrés.
  // Partagée entre les vignettes et le visualiseur plein écran, et persistée côté serveur.
  const [rotations, setRotations] = useState({});
  const [showLetter, setShowLetter] = useState(false);
  const [showJex, setShowJex] = useState(false);
  // Audit d'écart de score : compare ce dossier à un autre
  const [compareId, setCompareId] = useState('');
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [leadTier, setLeadTier] = useState(null);
  const [leadValuation, setLeadValuation] = useState('');
  const [leadNote, setLeadNote] = useState('');
  const [leadInitialized, setLeadInitialized] = useState(null);
  const [savingLead, setSavingLead] = useState(false);
  const [leadCalls, setLeadCalls] = useState([]);
  const [newCallStatus, setNewCallStatus] = useState('qualifie');
  const [newCallNote, setNewCallNote] = useState('');
  const [addingCall, setAddingCall] = useState(false);
  // Suivi d'appel (micro-CRM, indépendant de "À rappeler")
  const [answered, setAnswered] = useState(false);
  const [wantsLawyer, setWantsLawyer] = useState(false);
  const [crmComment, setCrmComment] = useState('');

  // Sync state when dossier changes
  if (dossier && dossier.id !== leadInitialized) {
    setLeadTier(dossier.lead_tier || null);
    setLeadValuation(dossier.lead_valuation || '');
    setLeadNote(dossier.lead_admin_note || '');
    setLeadCalls(dossier.lead_calls || []);
    setAnswered(!!dossier.answered);
    setWantsLawyer(!!dossier.wants_lawyer);
    setCrmComment(dossier.admin_comment || '');
    const initialRotations = {};
    (dossier.files || []).forEach((f) => {
      if (f.rotation) initialRotations[f.file_id] = f.rotation;
    });
    setRotations(initialRotations);
    setLeadInitialized(dossier.id);
  }

  if (!dossier) return null;

  const handleRetriggerLV3 = async () => {
    setIsRetriggering(true);
    try {
      await adminAPI.retriggerLV3(dossier.id);
      toast.success('Pipeline IA relancé ! L\'analyse sera disponible dans 1 à 3 minutes.');
      // Refresh the dossier data after a delay
      if (onDossierUpdated) {
        setTimeout(() => onDossierUpdated(), 45000); // Refresh after 45s
      }
    } catch (error) {
      console.error('Erreur relance pipeline:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors du lancement de l\'analyse';
      toast.error(errorMessage);
    } finally {
      setIsRetriggering(false);
    }
  };

  const handlePickFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    // Append plutôt que remplacer : l'admin peut sélectionner en plusieurs fois
    setPendingFiles((prev) => [...prev, ...picked]);
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadFiles = async () => {
    if (pendingFiles.length === 0) return;
    setIsUploadingFiles(true);
    try {
      const { data } = await adminAPI.addDossierFiles(dossier.id, pendingFiles, relaunchAfterUpload);

      toast.success(
        `${data.added_files_count} pièce(s) ajoutée(s) — ${data.total_files_count} document(s) au dossier.` +
        (data.pipeline_relaunched
          ? ' Pipeline relancé sur l\'ensemble, analyse disponible dans 1 à 3 minutes.'
          : '')
      );
      if (data.skipped_files?.length > 0) {
        toast.warning(`Fichier(s) vide(s) ignoré(s) : ${data.skipped_files.join(', ')}`);
      }

      setPendingFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';

      if (onDossierUpdated) {
        onDossierUpdated(); // rafraîchit tout de suite la liste des documents
        if (data.pipeline_relaunched) {
          setTimeout(() => onDossierUpdated(), 45000); // puis la nouvelle analyse
        }
      }
    } catch (error) {
      console.error('Erreur ajout de pièces:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout des pièces');
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleCompare = async () => {
    if (!compareId.trim()) return;
    setComparing(true);
    setComparison(null);
    try {
      const { data } = await adminAPI.compareDossiers(dossier.id, compareId.trim());
      setComparison(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Comparaison impossible');
    } finally {
      setComparing(false);
    }
  };

  const handleSaveLead = async () => {
    setSavingLead(true);
    try {
      const data = {
        lead_tier: leadTier,
        lead_valuation: leadValuation ? parseFloat(leadValuation) : null,
        lead_admin_note: leadNote || null,
        lead_status: leadTier ? 'available' : null,
      };
      await adminAPI.updateLead(dossier.id, data);
      toast.success(leadTier ? 'Lead publié sur la marketplace' : 'Lead retiré de la marketplace');
      if (onDossierUpdated) onDossierUpdated();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingLead(false);
    }
  };

  const handleAddCall = async () => {
    setAddingCall(true);
    try {
      const entry = await adminAPI.addLeadCall(dossier.id, {
        status: newCallStatus,
        note: newCallNote || null,
      });
      setLeadCalls([...leadCalls, entry.data]);
      setNewCallNote('');
      toast.success('Appel enregistré');
      if (onDossierUpdated) onDossierUpdated();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement de l'appel");
    } finally {
      setAddingCall(false);
    }
  };

  const handleToggleCrm = async (field) => {
    const current = field === 'answered' ? answered : wantsLawyer;
    const next = !current;
    // Maj optimiste
    if (field === 'answered') setAnswered(next);
    else setWantsLawyer(next);
    try {
      await adminAPI.updateDossierCrm(dossier.id, { [field]: next });
      if (onDossierUpdated) onDossierUpdated();
    } catch (error) {
      // Rollback en cas d'échec
      if (field === 'answered') setAnswered(current);
      else setWantsLawyer(current);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleRotate = async (fileId, degrees) => {
    const previous = rotations[fileId] || 0;
    // Maj optimiste : la vignette et le visualiseur pivotent instantanément.
    setRotations((prev) => ({ ...prev, [fileId]: degrees }));
    try {
      await adminAPI.updateFileRotation(dossier.id, fileId, degrees);
    } catch (error) {
      setRotations((prev) => ({ ...prev, [fileId]: previous }));
      toast.error('Erreur lors de la rotation de la pièce');
    }
  };

  const handleSaveCrmComment = async (text) => {
    const previous = crmComment;
    setCrmComment(text);
    try {
      await adminAPI.updateDossierCrm(dossier.id, { admin_comment: text });
      toast.success('Commentaire sauvegardé');
      if (onDossierUpdated) onDossierUpdated();
    } catch (error) {
      setCrmComment(previous);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Extract AI analysis data
  const jsonAnalysis = dossier.json_analysis || {};
  const lv1Data = jsonAnalysis.LV1_score;
  const lv2Data = jsonAnalysis.LV2_diagnostic;
  const lv3Data = jsonAnalysis.LV3_analyse_juridique_payante;
  const hasAnyAnalysis = lv1Data || lv2Data || lv3Data;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getScoreColor = (score) => {
    // score brut IA : 0-9.5 (NOT 0-100). Seuils alignés sur l'échelle native.
    if (score <= 3) return 'bg-red-100 text-red-800 border-red-300';
    if (score <= 7) return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-green-100 text-green-800 border-green-300';
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copie`);
  };

  const hasInvalidDocuments = dossier.documents_validity && !dossier.documents_validity.is_valid;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <User size={24} className="text-blue-900" />
            {dossier.nom || 'Sans nom'}
            <span className="text-sm font-normal text-gray-500">
              #{dossier.id?.slice(0, 8)}
            </span>
          </DialogTitle>
          <DialogDescription>
            Dossier soumis le {formatDate(dossier.created_at)}
          </DialogDescription>
        </DialogHeader>

        {/* Document validity warning */}
        {hasInvalidDocuments && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-orange-800">Documents potentiellement invalides</p>
                <p className="text-sm text-orange-700 mt-1">
                  {dossier.documents_validity.warning_message}
                </p>
                {dossier.documents_validity.invalid_documents?.length > 0 && (
                  <ul className="mt-2 text-sm text-orange-600">
                    {dossier.documents_validity.invalid_documents.map((doc, i) => (
                      <li key={i}>{doc.filename}: {doc.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Score non exploitable : l'échec doit sauter aux yeux, il était auparavant
            masqué derrière un 5.0 écrit silencieusement par le sanitizer. */}
        {dossier.score_status === 'failed' && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-red-900">Score non exploitable</p>
              <p className="text-xs text-red-700 mt-0.5">
                {dossier.json_analysis?.LV1_score?.score_status_reason
                  || "Le modèle n'a pas produit de score lisible."}{' '}
                Relancez le pipeline, ou ajoutez une pièce plus lisible.
              </p>
            </div>
          </div>
        )}
        {dossier.score_status === 'clamped' && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">Score hors bornes, ramené</p>
              <p className="text-xs text-amber-700 mt-0.5">
                {dossier.json_analysis?.LV1_score?.score_status_reason}
              </p>
            </div>
          </div>
        )}

        {/* Relancer le pipeline IA — toujours visible (utile surtout quand l'analyse a échoué) */}
        <div className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Brain size={18} className="text-purple-600" />
            <div>
              <p className="text-sm font-medium text-purple-900">Pipeline d'analyse IA</p>
              <p className="text-xs text-purple-700">
                {hasAnyAnalysis
                  ? 'Relancer pour régénérer l\'analyse (nouveaux prompts, OCR amélioré…)'
                  : '⚠️ Aucune analyse pour ce dossier — relancer le pipeline'}
              </p>
            </div>
          </div>
          <Button
            variant={hasAnyAnalysis ? 'outline' : 'default'}
            size="sm"
            onClick={handleRetriggerLV3}
            disabled={isRetriggering}
            className={hasAnyAnalysis ? '' : 'bg-purple-600 hover:bg-purple-700 text-white'}
          >
            <RefreshCw size={14} className={`mr-2 ${isRetriggering ? 'animate-spin' : ''}`} />
            {isRetriggering ? 'Lancement…' : 'Relancer le pipeline IA'}
          </Button>
        </div>

        {/* Comparer à un autre dossier — instruire un écart de score */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList size={18} className="text-slate-600" />
            <div>
              <p className="text-sm font-medium text-slate-900">Comparer à un autre dossier</p>
              <p className="text-xs text-slate-600">
                Empreintes des pièces, texte OCR et tokens — pour savoir d'où vient un écart de score
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              value={compareId}
              onChange={(e) => setCompareId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCompare()}
              placeholder="Identifiant ou préfixe (ex. dede77e5)"
              className="text-sm"
            />
            <Button variant="outline" size="sm" onClick={handleCompare} disabled={comparing || !compareId.trim()}>
              {comparing ? 'Analyse…' : 'Comparer'}
            </Button>
          </div>

          {comparison && (
            <div className="mt-4 space-y-3">
              <div className={`p-3 rounded-lg border text-sm ${
                comparison.diff.pieces_identiques
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-blue-50 border-blue-200 text-blue-900'
              }`}>
                <p className="font-medium">
                  {comparison.diff.pieces_identiques
                    ? 'Pièces strictement identiques (empreintes SHA-256)'
                    : `${comparison.diff.nb_pieces_communes} pièce(s) commune(s) sur des lots différents`}
                </p>
                <p className="text-xs mt-1">{comparison.diff.piste}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {[['a', dossier.id], ['b', compareId]].map(([cle, libelle]) => {
                  const d = comparison[cle];
                  return (
                    <div key={cle} className="p-3 bg-white border border-gray-200 rounded-lg">
                      <p className="text-xs text-gray-500 truncate">{d.id}</p>
                      <p className="text-lg font-bold" style={{ color: '#1e3a8a' }}>
                        {isScoreExploitable(d.score) ? `${transformScore(d.score)}%` : '—'}
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          brut {d.score ?? 'n/a'}
                        </span>
                      </p>
                      <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                        <li>{d.nb_pieces} pièce(s)</li>
                        <li>Agent 2 : {d.tokens.agent2_input ?? '?'} tokens en entrée</li>
                        <li>Statut : {d.score_status || 'non tracé'}</li>
                        <li>{d.nb_analyses_tracees} analyse(s) tracée(s)</li>
                      </ul>
                    </div>
                  );
                })}
              </div>

              {comparison.diff.ecart_score_affiche != null && (
                <p className="text-xs text-gray-600">
                  Écart : {comparison.diff.ecart_score_brut} point(s) brut(s), soit{' '}
                  {comparison.diff.ecart_score_affiche} points affichés.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Courrier de demande de pièces — génération admin (bypass paiement) */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Courrier de demande de pièces</p>
                <p className="text-xs text-blue-700">
                  Génère le courrier prêt à poster à partir de l'analyse du dossier
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowLetter((v) => !v)}>
              <FileText size={14} className="mr-2" />
              {showLetter ? 'Masquer le courrier' : 'Générer le courrier'}
            </Button>
          </div>
          {showLetter && (
            <div className="mt-4">
              <DemandePiecesLetter dossierId={dossier.id} force />
            </div>
          )}
        </div>

        {/* Contestation devant le juge de l'exécution — génération admin (bypass paiement) */}
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scale size={18} className="text-indigo-600" />
              <div>
                <p className="text-sm font-medium text-indigo-900">Contestation devant le juge de l'exécution</p>
                <p className="text-xs text-indigo-700">
                  Échéancier + lettre de dénonciation au commissaire de justice qui a saisi
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowJex((v) => !v)}>
              <Scale size={14} className="mr-2" />
              {showJex ? 'Masquer le pack' : 'Générer le pack'}
            </Button>
          </div>
          {showJex && (
            <div className="mt-4">
              <SaisineJexPack dossierId={dossier.id} force />
            </div>
          )}
        </div>

        {/* Consentement RGPD — visible toujours, déterminant pour la marketplace */}
        <div className={`p-3 rounded-lg border flex items-start gap-3 ${
          dossier.consent_lawyer_share
            ? 'bg-green-50 border-green-200'
            : 'bg-red-50 border-red-200'
        }`}>
          {dossier.consent_lawyer_share ? (
            <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="text-sm">
            {dossier.consent_lawyer_share ? (
              <>
                <p className="font-medium text-green-900">Consentement au partage avocat : donné</p>
                <p className="text-xs text-green-700 mt-0.5">
                  Coché au dépôt
                  {dossier.consent_lawyer_share_at && ` le ${formatDate(dossier.consent_lawyer_share_at)}`}
                  {dossier.consent_lawyer_share_ip && ` (IP ${dossier.consent_lawyer_share_ip})`}
                </p>
              </>
            ) : (
              <>
                <p className="font-medium text-red-900">⚠️ Consentement RGPD absent</p>
                <p className="text-xs text-red-700 mt-0.5">
                  Ce dossier ne peut pas être publié sur la marketplace avocats — même valorisé, il restera invisible
                  aux avocats. Soit le client n'a pas coché la case, soit le dossier date d'avant le déploiement du flux
                  consentement (11/05/2026).
                </p>
              </>
            )}
          </div>
        </div>

        {/* Suivi d'appel (micro-CRM) — trace des échanges téléphoniques */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-3">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <PhoneCall size={18} />
            Suivi d'appel
          </h3>
          <CrmCallTracking
            answered={answered}
            wantsLawyer={wantsLawyer}
            comment={crmComment}
            onToggle={handleToggleCrm}
            onSaveComment={handleSaveCrmComment}
          />
        </div>

        {/* Contact & Status */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Contact Info */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Mail size={18} />
              Contact
            </h3>

            {/* Email */}
            <div className="flex items-center justify-between">
              <a
                href={`mailto:${dossier.email}`}
                className="text-blue-600 hover:underline"
              >
                {dossier.email}
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(dossier.email, 'Email')}
              >
                <Copy size={14} />
              </Button>
            </div>

            {/* Phone */}
            {dossier.telephone && (
              <div className="flex items-center justify-between">
                <a
                  href={`tel:${dossier.telephone}`}
                  className="flex items-center gap-2 text-blue-600 hover:underline"
                >
                  <Phone size={16} />
                  {dossier.telephone}
                </a>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(dossier.telephone, 'Telephone')}
                  >
                    <Copy size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <a href={`tel:${dossier.telephone}`}>
                      <Phone size={14} className="mr-1" />
                      Appeler
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Status Info */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <CreditCard size={18} />
              Statut
            </h3>

            {/* Payment Status */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Paiement:</span>
              <Badge variant={dossier.payment_status === 'paid' ? 'default' : 'secondary'}>
                {dossier.payment_status === 'paid' ? (
                  <>
                    <CheckCircle2 size={14} className="mr-1" />
                    Paye
                  </>
                ) : (
                  'En attente'
                )}
              </Badge>
            </div>

            {/* Score */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Score brut IA:</span>
              {dossier.score !== null && dossier.score !== undefined ? (
                <span className={`px-3 py-1 rounded-full font-bold border ${getScoreColor(dossier.score)}`}>
                  {Math.round((dossier.score || 0) * 10)}/100
                </span>
              ) : (
                <span className="text-gray-400">En cours d'analyse...</span>
              )}
            </div>

            {/* Displayed Score */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Score affiché:</span>
              {dossier.score !== null && dossier.score !== undefined ? (
                <span className="px-3 py-1 rounded-full font-bold border bg-green-100 text-green-800 border-green-300">
                  {isScoreExploitable(dossier.score)
                    ? `${transformScore(dossier.score)}%`
                    : '—'}
                </span>
              ) : (
                <span className="text-gray-400">—</span>
              )}
            </div>

            {/* Date */}
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="flex items-center gap-1 text-gray-800">
                <Calendar size={14} />
                {formatDate(dossier.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* AI Analysis Pipeline Output */}
        {hasAnyAnalysis && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Brain size={18} className="text-purple-600" />
              Analyse IA du pipeline
            </h3>

            {/* LV1 - Score & Interpretation */}
            {lv1Data && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowLV1(!showLV1)}
                  className="w-full p-4 bg-blue-50 hover:bg-blue-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🎯</span>
                    <div className="text-left">
                      <h4 className="font-semibold text-blue-900">LV1 - Score de probabilité</h4>
                      <p className="text-sm text-blue-700">
                        Score brut: {lv1Data.score_evitement_paiement}/10
                        ({Math.round(lv1Data.score_evitement_paiement * 10)}/100)
                        — Affiché: {isScoreExploitable(dossier.score)
                          ? `${transformScore(dossier.score)}%`
                          : 'indisponible'}
                      </p>
                    </div>
                  </div>
                  {showLV1 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {showLV1 && lv1Data.interpretation && (
                  <div className="p-4 bg-white border-t">
                    <p className="text-sm font-medium text-gray-600 mb-2">Interprétation :</p>
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {lv1Data.interpretation}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* LV2 - Diagnostic */}
            {lv2Data && Object.keys(lv2Data).length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowLV2(!showLV2)}
                  className="w-full p-4 bg-yellow-50 hover:bg-yellow-100 flex items-center justify-between transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <ClipboardList size={24} className="text-yellow-700" />
                    <div className="text-left">
                      <h4 className="font-semibold text-yellow-900">LV2 - Diagnostic gratuit</h4>
                      <p className="text-sm text-yellow-700">
                        {lv2Data.points_de_vigilance?.length || 0} points de vigilance •
                        {lv2Data.pieces_manquantes_necessaires?.length || 0} pièces manquantes
                      </p>
                    </div>
                  </div>
                  {showLV2 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {showLV2 && (
                  <div className="p-4 bg-white border-t">
                    <LV2Diagnostic lv2Data={lv2Data} />
                  </div>
                )}
              </div>
            )}

            {/* LV3 - Analyse juridique (visible admin même si non payé) */}
            {lv3Data && Object.keys(lv3Data).length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowLV3(!showLV3)}
                  className={`w-full p-4 flex items-center justify-between transition-colors ${
                    dossier.payment_status === 'paid'
                      ? 'bg-green-50 hover:bg-green-100'
                      : 'bg-purple-50 hover:bg-purple-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Scale size={24} className={
                      dossier.payment_status === 'paid' ? 'text-green-700' : 'text-purple-700'
                    } />
                    <div className="text-left">
                      <h4 className={`font-semibold ${
                        dossier.payment_status === 'paid' ? 'text-green-900' : 'text-purple-900'
                      }`}>
                        LV3 - Analyse juridique
                        {dossier.payment_status !== 'paid' && (
                          <Badge variant="outline" className="ml-2 text-xs bg-purple-100 text-purple-700 border-purple-300">
                            Vue admin
                          </Badge>
                        )}
                      </h4>
                      <p className={`text-sm ${
                        dossier.payment_status === 'paid' ? 'text-green-700' : 'text-purple-700'
                      }`}>
                        {dossier.payment_status === 'paid'
                          ? '✅ Payé - Client a accès'
                          : '👁️ Visible admin uniquement - Client doit payer'
                        }
                      </p>
                    </div>
                  </div>
                  {showLV3 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {showLV3 && (
                  <div className="p-4 bg-white border-t">
                    {dossier.payment_status !== 'paid' && (
                      <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-sm text-purple-800 flex items-center gap-2">
                          <AlertTriangle size={16} />
                          <span><strong>Vue admin :</strong> Le client ne voit pas cette analyse tant qu'il n'a pas payé.</span>
                        </p>
                      </div>
                    )}
                    <LV3LegalAnalysis
                      lv3Data={lv3Data}
                      isAdmin={true}
                      onRetriggerAnalysis={handleRetriggerLV3}
                      isRetriggering={isRetriggering}
                    />
                    {/* Bouton admin pour relancer le pipeline (test de prompts) */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          Admin : Relancer l'analyse pour tester différentes versions de prompts
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRetriggerLV3}
                          disabled={isRetriggering}
                          className="text-purple-700 border-purple-300 hover:bg-purple-50"
                        >
                          <RefreshCw size={14} className={`mr-2 ${isRetriggering ? 'animate-spin' : ''}`} />
                          {isRetriggering ? 'En cours...' : 'Relancer le pipeline'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* No analysis available */}
        {!hasAnyAnalysis && (
          <div className="p-6 bg-gray-50 rounded-lg text-center">
            <Brain size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucune analyse IA disponible</p>
            <p className="text-sm text-gray-400 mt-1">L'analyse est en cours ou n'a pas encore été lancée.</p>
          </div>
        )}

        {/* Lead Valorization */}
        <div className="p-4 border-2 border-amber-200 bg-amber-50 rounded-lg space-y-4">
          <h3 className="font-semibold text-amber-900 flex items-center gap-2">
            <Trophy size={18} />
            Valorisation Lead — Marketplace Avocats
            {dossier.lead_status === 'sold' && (
              <Badge className="ml-2 bg-red-100 text-red-800 border-red-300">Vendu</Badge>
            )}
            {dossier.lead_status === 'available' && dossier.consent_lawyer_share && (
              <Badge className="ml-2 bg-green-100 text-green-800 border-green-300">En vente</Badge>
            )}
            {dossier.lead_status === 'available' && !dossier.consent_lawyer_share && (
              <Badge className="ml-2 bg-red-100 text-red-800 border-red-300">
                Publié mais invisible (consent. RGPD manquant)
              </Badge>
            )}
            {dossier.lead_valuation_needs_review && (
              <Badge className="ml-2 bg-orange-100 text-orange-800 border-orange-300">
                À valoriser manuellement
              </Badge>
            )}
            {dossier.lead_valuation_set_by === 'ai_pipeline' && !dossier.lead_valuation_needs_review && (
              <Badge className="ml-2 bg-blue-100 text-blue-800 border-blue-300">
                Pré-valorisé par IA
              </Badge>
            )}
          </h3>

          {/* Lead économiquement non viable — honoraires avocat > valeur évitée client */}
          {dossier.json_analysis?.valorisation_lead && dossier.json_analysis.valorisation_lead.economically_viable === false && (
            <div className="p-3 bg-orange-50 border border-orange-300 rounded text-sm text-orange-900 flex items-start gap-2">
              <AlertTriangle size={16} className="text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Lead économiquement non viable — non publié automatiquement</p>
                <p className="text-xs mt-1">
                  {dossier.json_analysis.valorisation_lead.non_viable_reason || 'Honoraires avocat supérieurs à la valeur évitée pour le client.'}
                </p>
                <p className="text-xs mt-1 italic">
                  Tu peux forcer la publication manuellement en sélectionnant un tier ci-dessous, mais l'avocat aura peu de chances de convertir ce lead en client.
                </p>
              </div>
            </div>
          )}

          {!dossier.consent_lawyer_share && (
            <div className="p-3 bg-red-50 border border-red-300 rounded text-sm text-red-800 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Ce lead ne pourra pas être vendu :</strong> le client n'a pas (ou n'avait pas) donné
                son consentement RGPD au partage avec un avocat. Tu peux valoriser et publier, mais il restera
                invisible côté avocat tant que <code>consent_lawyer_share</code> n'est pas à <code>true</code>.
              </span>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classification</label>
              <Select value={leadTier || 'none'} onValueChange={(v) => setLeadTier(v === 'none' ? null : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Non classé —</SelectItem>
                  <SelectItem value="gold">Or (150€)</SelectItem>
                  <SelectItem value="silver">Argent (70€)</SelectItem>
                  <SelectItem value="bronze">Bronze (15€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Euro size={14} className="inline mr-1" />
                Valorisation estimée (€)
              </label>
              <Input
                type="number"
                placeholder="ex: 3000"
                value={leadValuation}
                onChange={(e) => setLeadValuation(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleSaveLead}
                disabled={savingLead || dossier.lead_status === 'sold'}
                className="w-full"
              >
                {savingLead ? 'Sauvegarde...' : leadTier ? 'Publier sur la marketplace' : 'Retirer de la marketplace'}
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center justify-between">
              <span>
                <MessageSquare size={14} className="inline mr-1" />
                Note pour l'avocat (visible avant achat, sur la carte lead)
              </span>
              {dossier.lead_valuation_set_by === 'ai_pipeline' && (
                <span className="text-xs text-blue-700 font-normal">
                  Pré-remplie par l'IA — tu peux éditer
                </span>
              )}
            </label>
            <textarea
              value={leadNote}
              onChange={(e) => setLeadNote(e.target.value)}
              placeholder="Observations sur les pièces, montant à récupérer, contexte du dossier..."
              className="w-full p-2 text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 font-mono"
              rows={8}
            />
            {dossier.json_analysis?.valorisation_lead?.montant_creance_total_eur && (
              <p className="text-xs text-gray-600 mt-1">
                Estimation IA : créance {dossier.json_analysis.valorisation_lead.montant_creance_total_eur.toLocaleString('fr-FR')} € ·
                proba gain {Math.round((dossier.json_analysis.valorisation_lead.probabilite_gain || 0) * 100)}% ·
                valeur évitée client ~{dossier.json_analysis.valorisation_lead.valeur_evitee_client_eur?.toLocaleString('fr-FR') ?? '?'} € ·
                espérance avocat ~{dossier.json_analysis.valorisation_lead.honoraires_avocat_estimes_eur?.toLocaleString('fr-FR') ?? '?'} €
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <PhoneCall size={14} className="inline mr-1" />
              Historique d'appels (visible avant achat, sur la carte lead)
            </label>

            {leadCalls.length > 0 && (
              <div className="space-y-1 mb-2">
                {leadCalls.map((call) => (
                  <div key={call.id} className="flex items-start gap-2 text-sm bg-white border border-amber-200 rounded-md p-2">
                    <Badge
                      className={
                        call.status === 'qualifie'
                          ? 'bg-green-100 text-green-800 border-green-300'
                          : 'bg-gray-200 text-gray-700 border-gray-300'
                      }
                    >
                      {LEAD_CALL_STATUS_LABELS[call.status] || call.status}
                    </Badge>
                    <span className="text-xs text-gray-400 shrink-0">
                      {new Date(call.called_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {call.note && <span className="text-gray-700">{call.note}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 items-start">
              <Select value={newCallStatus} onValueChange={setNewCallStatus}>
                <SelectTrigger className="w-40 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qualifie">Qualifié</SelectItem>
                  <SelectItem value="injoignable">Injoignable</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Ce que le client a dit / infos collectées..."
                value={newCallNote}
                onChange={(e) => setNewCallNote(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleAddCall} disabled={addingCall} className="shrink-0">
                {addingCall ? 'Ajout...' : 'Enregistrer l\'appel'}
              </Button>
            </div>
          </div>
        </div>

        {/* Documents */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <FileText size={18} />
              Documents ({dossier.files?.length || 0})
            </h3>
            <div className="flex items-center gap-2">
              {dossier.files?.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setViewerIndex(0)}>
                  <Images size={14} className="mr-2" />
                  Visionner toutes les pièces
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingFiles}
              >
                <Upload size={14} className="mr-2" />
                Ajouter des pièces
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
              onChange={handlePickFiles}
              className="hidden"
            />
          </div>

          {/* Pièces sélectionnées, en attente d'envoi */}
          {pendingFiles.length > 0 && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm font-medium text-amber-900 mb-2">
                {pendingFiles.length} pièce(s) à rattacher au dossier
              </p>

              <ul className="space-y-1 mb-3">
                {pendingFiles.map((file, index) => (
                  <li
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between gap-2 text-sm text-amber-900 bg-white/60 rounded px-2 py-1"
                  >
                    <span className="truncate">{file.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-amber-700">
                        {(file.size / 1024).toFixed(0)} Ko
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemovePendingFile(index)}
                        disabled={isUploadingFiles}
                        className="text-amber-700 hover:text-amber-900 disabled:opacity-50"
                        aria-label={`Retirer ${file.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <label className="flex items-start gap-2 text-sm text-amber-900 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={relaunchAfterUpload}
                  onChange={(e) => setRelaunchAfterUpload(e.target.checked)}
                  disabled={isUploadingFiles}
                  className="mt-0.5"
                />
                <span>
                  Relancer le pipeline IA après l'ajout
                  <span className="block text-xs text-amber-700">
                    L'analyse est régénérée sur la totalité du dossier — anciens et nouveaux documents.
                  </span>
                </span>
              </label>

              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleUploadFiles} disabled={isUploadingFiles}>
                  <Upload size={14} className={`mr-2 ${isUploadingFiles ? 'animate-pulse' : ''}`} />
                  {isUploadingFiles ? 'Envoi…' : `Rattacher ${pendingFiles.length} pièce(s)`}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPendingFiles([]);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  disabled={isUploadingFiles}
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {dossier.files && dossier.files.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {dossier.files.map((file, index) => (
                <div key={file.file_id || index} className="relative">
                  {file.added_by === 'admin' && (
                    <Badge
                      variant="secondary"
                      className="absolute top-2 right-2 z-10 text-[10px] bg-amber-100 text-amber-800 border border-amber-200"
                    >
                      Ajouté par l'admin
                    </Badge>
                  )}
                  <DocumentPreview
                    dossierId={dossier.id}
                    file={file}
                    rotation={rotations[file.file_id] || 0}
                    onRotate={(degrees) => handleRotate(file.file_id, degrees)}
                    onOpen={() => setViewerIndex(index)}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 bg-gray-50 rounded-lg text-center">
              <FileText size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">Aucun document</p>
            </div>
          )}

          <DocumentViewer
            dossierId={dossier.id}
            files={dossier.files || []}
            open={viewerIndex !== null}
            initialIndex={viewerIndex ?? 0}
            onClose={() => setViewerIndex(null)}
            rotations={rotations}
            onRotate={handleRotate}
          />
        </div>

        {/* View Full Dossier Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button asChild>
            <a href={`/dossier/${dossier.id}`} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={16} className="mr-2" />
              Ouvrir le dossier complet
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDossierDetail;
